import { Box, LinearProgress } from "@mui/material";
import { useDebounceFn } from "ahooks";
import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useGetTrackUploadPercentage } from "../../../atoms/supplychain/supplyChainMultiUploadProgressAtom";
import { useGetTrackFileValidationStatus } from "../../../atoms/supplychain/supplyChainPendingUploadsAtom";
import { CreateReleaseFlow } from "../../../store/models/release";
import { Text } from "../../core-ui/components/Text/Text";
import { TextStyleVariant } from "../../core-ui/components/Text/TextUtils";
import {
  ReleaseTrackStatus,
  ReleaseTrackStatusVariant,
} from "./ReleaseTrackStatus";

interface WrappedReleaseTrackStatusProps {
  releaseTrackId: number;
  isUploadVerified: boolean;
  isFree?: boolean;
}

export const WrappedReleaseTrackStatus = ({
  releaseTrackId,
  isUploadVerified,
  isFree,
}: WrappedReleaseTrackStatusProps) => {
  const fileValidating = useGetTrackFileValidationStatus(releaseTrackId);
  const uploadPercent = useGetTrackUploadPercentage(releaseTrackId);
  const shouldShowFailed =
    !fileValidating &&
    !isUploadVerified &&
    (uploadPercent === undefined || uploadPercent === 100);
  const [debouncedFailedUpload, setDebouncedFailedUpload] = useState(false);

  const { run: updateFailedUpload } = useDebounceFn(
    (shouldShowFailed: boolean) => {
      setDebouncedFailedUpload(shouldShowFailed);
    },
    { wait: 1500 },
  );

  // Ensures that "Upload Failed" is not flashed in between status changes
  useEffect(() => {
    // If conditions change to not failed, update immediately
    // If conditions change to failed, wait 1 second
    if (!shouldShowFailed && debouncedFailedUpload) {
      setDebouncedFailedUpload(false);
    } else if (shouldShowFailed && !debouncedFailedUpload) {
      updateFailedUpload(true);
    }
  }, [debouncedFailedUpload, shouldShowFailed, updateFailedUpload]);

  const { flow } = useParams<{ flow: CreateReleaseFlow }>();
  const isFreeRelease = Boolean(flow === CreateReleaseFlow.FREE || isFree);

  const statusVariant = useMemo(() => {
    // SupplyChain status does not apply to Free Releases
    if (isFreeRelease) return ReleaseTrackStatusVariant.SUBMITTED;
    if (isUploadVerified) return ReleaseTrackStatusVariant.VERIFIED_PASSED;
    if (debouncedFailedUpload) return ReleaseTrackStatusVariant.VERIFIED_FAILED;
    if (fileValidating || uploadPercent === 100)
      return ReleaseTrackStatusVariant.PENDING_REVIEW;
    return ReleaseTrackStatusVariant.NOT_SUBMITTED;
  }, [
    isFreeRelease,
    isUploadVerified,
    debouncedFailedUpload,
    fileValidating,
    uploadPercent,
  ]);

  const isUploading = useMemo(
    () => uploadPercent > 0 && uploadPercent < 100,
    [uploadPercent],
  );

  return isUploading ? (
    <Box alignItems="center" display="flex" gap={1}>
      <Box width="100%">
        <LinearProgress variant="determinate" value={uploadPercent} />
      </Box>
      <Text variant={TextStyleVariant.P3}>{uploadPercent.toFixed(0)}%</Text>
    </Box>
  ) : (
    <ReleaseTrackStatus variant={statusVariant} />
  );
};
